/**
 * PropertyDetails — the right-column "Property Details" card (Figma 192:97).
 *
 * The top of the card lists the property's core specs (moved here from the main
 * column): internal floor space, build, terrace, garden, build year, and the
 * community / basura / IBI fees.
 *   - Size stats (floor space/build/terrace/garden) hide when the feed has no data.
 *   - Build Year + fees: resale always shows the row (unknown → "TBC"); on
 *     developments the row is hidden when the value is unknown.
 *
 * Below them, which rows are *applicable* depends on type / development:
 *   - Floor   (RsFloors)  → hidden for plots and developments
 *   - Storeys (RsStoreys) → apartments only
 *   - Tourist Licence     → hidden for developments
 *   - First Occupancy License → resales only (hidden on developments/rentals)
 *   - Key Ready / Mortgage Available / Guaranteed Rental / Contract Assignment Before Completion
 *     / Reservation / Contract / Completion → new developments only
 * These applicable rows always render; when the value is missing/blank they show
 * "Not Known" rather than disappearing.
 *
 * Props: offer (uses offer.stats, offer.unit, offer.buildYear, offer.fees,
 *               offer.propertyType, offer.details, offer.development)
 */
import React from 'react';
import { useStrings } from './i18n';

// A label/value row. Renders nothing when the value is empty (keeps the card tidy).
function Row({ label, value }) {
    if (value === '' || value === null || value === undefined) return null;
    return (
        <div className="rs-pd__row">
            <span className="rs-pd__k">{label}</span>
            <span className="rs-pd__v">{value}</span>
        </div>
    );
}

export default function PropertyDetails({ offer }) {
    const t = useStrings();
    const d = offer.details || {};
    const dev = offer.development;
    const stats = offer.stats || {};
    const fees = offer.fees || {};
    const u = offer.unit || 'm²';
    const type = (offer.propertyType || '').toLowerCase();
    const isApartment = type.includes('apartment');
    const isPlot = type.includes('plot') || type.includes('land');
    const isRental = !!offer.isRental; // long- or short-term rental

    // Size stat: a plain number or a {from, to} range object →
    // "from - to<unit>" for developments, "from<unit>" otherwise. Empty when
    // there's no size, so the Row is hidden (unlike floor/key-status below,
    // which fall back to "Not Known").
    const sv = (v) => {
        const from = v && typeof v === 'object' ? v.from : v;
        const to = v && typeof v === 'object' ? v.to : 0;
        if (!(from > 0)) return '';
        return to > from ? `${from} - ${to}${u}` : `${from}${u}`;
    };
    // Build Year / fees. Resale: always show the row — unknown values show "TBC".
    // Developments: only show when the feed has a real value (unknown → row hidden,
    // since these figures usually don't apply pre-completion).
    const feeVal = (v) => {
        const unknown = !v || v === 'Not Available' || v === t.notAvailable;
        if (!unknown) return v;
        // Resale unknowns show a localized placeholder ("TBC" in English, the
        // "Not Known" translation elsewhere); developments hide the row.
        return dev ? '' : t.tbc;
    };

    // A value, or "Not Known" when missing/blank. `zeroIsBlank` treats 0/"0" as
    // missing (used for numeric fields like RsFloors/RsStoreys).
    const orNotKnown = (v, zeroIsBlank) => {
        if (v === null || v === undefined || String(v).trim() === '') return t.notKnown;
        if (zeroIsBlank && (v === 0 || String(v) === '0')) return t.notKnown;
        return v;
    };
    // Boolean-ish (e.g. DevIsKeyReady) → Yes / No, or Not Known when absent.
    const yesNo = (v) => {
        if (v === null || v === undefined || String(v).trim() === '') return t.notKnown;
        const s = String(v).toLowerCase();
        return (v === true || s === '1' || s === 'true' || s === 'yes') ? t.yes : t.no;
    };
    // Boolean-ish with no "No" case (e.g. DevIsMortgageAvailable) — 1/truthy → Yes,
    // 0/blank → Not Provided, since the feed never sends an explicit "no" for these.
    const yesOrUnknown = (v) => (v && String(v) !== '0') ? t.yes : t.notProvided;
    const sellBeforeCompletion = (v) => {
        switch (parseInt(v, 10)) {
            case 1: return t.sellBeforeCompletionYes;
            case 2: return t.sellBeforeCompletionNo;
            default: return t.sellBeforeCompletionNotProvided;
        }
    };
    // Percentage (e.g. DevContractPercentage) → "20%", or the TBC placeholder when
    // 0/blank ("TBC" in English, the "Not Known" translation in other locales).
    const pct = (v) => (v === null || v === undefined || String(v).trim() === '' || String(v) === '0')
        ? t.tbc : `${v}%`;
    // First-occupancy licence: "1" → Yes, otherwise Not Known.
    const firstOcc = (d.firstOccupationLicence && String(d.firstOccupationLicence) !== '0') ? t.yes : t.notKnown;
    // Tourist (short-term) licence: RsShortTermLicence 0 Unknown / 1 Yes / 2 No.
    const touristLicence = (code) => {
        switch (parseInt(code, 10)) {
            case 1: return t.yes;
            case 2: return t.no;
            default: return t.notKnown; // 0 / unknown
        }
    };

    return (
        <div className="rs-card rs-pd">
            <p className="rs-pd__h">{t.propertyDetails}</p>
            <Row label={t.reference} value={offer.reference} />
            {/* Rentals show the security deposit right below the reference. */}
            {isRental && <Row label={t.securityDeposit} value={offer.securityDeposit} />}
            <Row label={t.intFloorSpace} value={sv(stats.intFloor)} />
            <Row label={t.build} value={sv(stats.build)} />
            <Row label={t.terrace} value={sv(stats.terrace)} />
            <Row label={t.garden} value={sv(stats.garden)} />
            <Row label={t.buildYear} value={feeVal(offer.buildYear)} />
            {/* Fees + occupancy/tourist licences don't apply to rentals — hidden. */}
            {!isRental && <Row label={t.communityFees} value={feeVal(fees.community)} />}
            {!isRental && <Row label={t.basuraTax} value={feeVal(fees.basura)} />}
            {!isRental && <Row label={t.ibiFees} value={feeVal(fees.ibi)} />}
            {!isPlot && !dev && <Row label={t.floors} value={(d.floor && String(d.floor) !== '0') ? d.floor : ''} />}
            {isApartment && !offer.isUnitOffer && <Row label={t.storeys} value={orNotKnown(d.storeys, true)} />}
            {!dev && !isRental && <Row label={t.touristLicence} value={touristLicence(d.shortTermLicence)} />}
            {!dev && !isRental && <Row label={t.firstOccupationLicence} value={firstOcc} />}
            {dev && <Row label={t.mortgageAvailable} value={yesOrUnknown(dev.mortgageAvailable)} />}
            {dev && <Row label={t.keyReady} value={yesNo(dev.keyReady)} />}
            {dev && <Row label={t.guaranteedRental} value={yesOrUnknown(dev.guaranteedRental)} />}
            {dev && <Row label={t.sellBeforeCompletion} value={sellBeforeCompletion(dev.soldBeforeCompletion)} />}
            {dev && <Row label={t.reservation} value={orNotKnown(dev.reservation)} />}
            {dev && <Row label={t.contract} value={pct(dev.contractPct)} />}
            {dev && <Row label={t.construction} value={pct(dev.constructionPct)} />}
            {dev && <Row label={t.completion} value={pct(dev.completionPct)} />}
        </div>
    );
}
